import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import moment from 'moment';
import { makeStyles } from '@material-ui/styles';
import {
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  Button,
  IconButton,
  TextField,
  Tooltip,
  Typography,
  colors
} from '@material-ui/core';
import { DatePicker } from '@material-ui/pickers';
import DeleteIcon from '@material-ui/icons/DeleteForever';
import EditIcon from '@material-ui/icons/Edit';

import { Label, DeleteModal } from 'components';
import { Fragment } from 'react';

const useStyles = makeStyles(theme => ({
  root: { borderRadius: 0 },
  header: {
    paddingBottom: 0,
    '& .MuiCardHeader-content': {
      display: 'flex',
      alignItems: 'center'
    }
  },
  content: {
    padding: 0,
    '&:last-child': {
      paddingBottom: 0
    }
  },
  description: {
    padding: theme.spacing(2, 3, 1, 3)
  },
  tags: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0, 3, 1, 3),
    '& > * + *': {
      marginLeft: theme.spacing(1)
    }
  },
  learnMoreButton: {
    marginLeft: theme.spacing(2)
  },
  likedButton: {
    color: colors.red[600]
  },
  shareButton: {
    marginLeft: theme.spacing(1)
  },
  details: {
    padding: theme.spacing(1, 3)
  }
}));

const GroupCard = props => {
  const { group, className, onEditGroup, onDeleteGroup, ...rest } = props;
  const classes = useStyles();
  const [openDelete, setOpenDelete] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [values, setValues] = useState(group);

  useEffect(() => {
    setValues(group);
    setEditMode(false);
  }, [group])

  const handleChange = ({ target }) => {
    setValues(state => ({ ...state, [target.name]: target.value }))
  }

  const close = () => setEditMode(false)

  const handleEdit = () => {
    onEditGroup(values, close);
  };

  const handleCloseDelete = () => {
    setOpenDelete(false)
  }

  const handleDeleteModal = () => {
    onDeleteGroup(group.classID, handleCloseDelete)
  }

  const handleCalendarChange = () => {/* no-op */ };

  const handleCalendarAccept = date => {
    setValues(values => ({
      ...values,
      expirationDate: date
    }));
  };

  return (
    <Card
      {...rest}
      className={clsx(classes.root, className)}
    >
      <DeleteModal
        content={<div>Are you sure you want to delete the group <strong>{group.name}</strong>?</div>}
        onClose={handleCloseDelete}
        onDelete={handleDeleteModal}
        open={openDelete}
        title={group.name}
      />
      <CardHeader
        className={classes.header}
        disableTypography
        subheader={<Fragment>
          {editMode ?
            <TextField
              autoFocus
              label="Name"
              margin="dense"
              name="name"
              onChange={handleChange}
              value={values.name || ''}
              variant="outlined"
            /> :
            <Typography
              color="textPrimary"
              display="inline"
              variant="h6"
            >
              {group.name}
            </Typography>}
          <Typography
            display="inline"
            variant="body2"
          >
            &nbsp;| Created: {moment.utc(group.created, 'MMM DD, YYYY h:mm:ss a').fromNow()}
          </Typography>
        </Fragment>
        }
        title={group.title}
      />
      <CardContent className={classes.content}>
        <div className={classes.description}>
          {editMode ?
            <TextField
              fullWidth
              label="Description"
              margin="dense"
              multiline
              name="description"
              onChange={handleChange}
              value={values.description || ''}
              variant="outlined"
            /> :
            <Typography
              colo="textSecondary"
              variant="subtitle2"
            >
              {group.description}
            </Typography>}
        </div>
        <div className={classes.tags}>
          {editMode && !group.students.length ?
            <TextField
              label="Code"
              margin="dense"
              name="code"
              onChange={handleChange}
              value={values.code || ''}
              variant="outlined"
            /> :
            <Label color={colors.blue[500]}>
              Group Code: {group.code}
            </Label>}
          {editMode &&
            <DatePicker
              format="YYYY/MM/DD"
              inputVariant="outlined"
              label="Expiration Date"
              margin="dense"
              minDate={moment()}
              onAccept={handleCalendarAccept}
              onChange={handleCalendarChange}
              value={moment(values.expirationDate, false)}
              variant="dialog"
            />
          }
        </div>
        <Divider />
        <div className={classes.details}>
          <Grid
            alignItems="center"
            container
            justify="space-between"
            spacing={3}
          >
            <Grid item>
              <Typography variant="h5">{moment.utc(group.expirationDate, false).fromNow()}</Typography>
              <Typography variant="body2">Expiration Date</Typography>
            </Grid>
            <Grid item>
              <Typography variant="h5">{group.students.length}</Typography>
              <Typography variant="body2">Members</Typography>
            </Grid>
            <Grid item>
              <Typography variant="h5">{group.repositories.length} Shared out</Typography>
              <Typography variant="body2">of {group.allRepositories.length} Repositories</Typography>
            </Grid>
            {editMode ?
              <Grid item>
                <Button
                  className={classes.learnMoreButton}
                  color="primary"
                  onClick={close}
                  variant="outlined"
                >
                  Cancel
                </Button>
                <Button
                  className={classes.learnMoreButton}
                  color="primary"
                  endIcon={<EditIcon />}
                  onClick={handleEdit}
                  variant="contained"
                >
                  Apply
                </Button>
              </Grid> :
              <Grid item>
                <Tooltip title="Edit Group Info">
                  <IconButton
                    className={classes.likeButton}
                    onClick={() => setEditMode(true)}
                    size="small"
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete Group">
                  <IconButton
                    className={classes.shareButton}
                    onClick={() => setOpenDelete(true)}
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </Grid>}
          </Grid>
        </div>
      </CardContent>
    </Card>
  );
};

GroupCard.propTypes = {
  className: PropTypes.string,
  group: PropTypes.object.isRequired,
  onDeleteGroup: PropTypes.func,
  onEditGroup: PropTypes.func
};

GroupCard.defaultProps = {
  group: {
    students: [],
    repositories: []
  }
};

export default GroupCard;
